import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaTimes,
  FaDownload,
  FaPrint,
  FaFileInvoiceDollar,
  FaEye,
  FaFilter
} from "react-icons/fa";

import {
  FileText,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  useNavigate,
} from "react-router-dom";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const API_BASE =
  "https://nissi-india.com/api";

const HOBillPassReport = () => {
  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  const navigate =
    useNavigate();

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showForm,
    setShowForm,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTERS
  ========================================================= */

  const [
    branch,
    setBranch,
  ] = useState("8");

  const [
    supplier,
    setSupplier,
  ] = useState("");

  const [
    bpNo,
    setBpNo,
  ] = useState("");

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  /* =========================================================
     DROPDOWN / DATA
  ========================================================= */

  const [
    branchesList,
    setBranchesList,
  ] = useState([]);

  const [
    suppliersList,
    setSuppliersList,
  ] = useState([]);

  const [
    results,
    setResults,
  ] = useState([]);

  const [
    grandTotal,
    setGrandTotal,
  ] = useState(0);

  /* =========================================================
     LOCAL STORAGE VALUES
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const branchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const branchType =
    (
      localStorage.getItem(
        "branchType"
      ) || "HO"
    ).toUpperCase();

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  const loginUserId =
    localStorage.getItem(
      "loginUserId"
    ) ||
    userId ||
    "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          branchId,

        "x-branch":
          branchType,

        "x-userid":
          userId,

        "x-loginuserid":
          loginUserId,
      }),
      [
        finYearId,
        finYear,
        branchId,
        branchType,
        userId,
        loginUserId,
      ]
    );

  /* =========================================================
     COMMON UI CLASSES
     SAME DESIGN AS DEPARTMENT MASTER
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     DATE HELPERS
  ========================================================= */

  const formatDateForApi =
    (dateStr) => {
      if (!dateStr) {
        return "";
      }

      const [
        year,
        month,
        day,
      ] =
        dateStr.split("-");

      return `${day}/${month}/${year}`;
    };

  const formatDate =
    (dateStr) => {
      if (!dateStr) {
        return "-";
      }

      const date =
        new Date(
          dateStr
        );

      return Number.isNaN(
        date.getTime()
      )
        ? dateStr
        : date.toLocaleDateString(
            "en-GB"
          );
    };

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency =
    (amount) => {
      if (
        amount === null ||
        amount ===
          undefined ||
        amount === ""
      ) {
        return "-";
      }

      return new Intl.NumberFormat(
        "en-IN",
        {
          style: "currency",
          currency: "INR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }
      ).format(
        Number(
          amount || 0
        )
      );
    };

  /* =========================================================
     CLEAR FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const clearFilters =
    () => {
      setBranch(
        branchId ||
          "8"
      );

      setSupplier("");

      setBpNo("");

      setFromDate("");

      setToDate("");

      setResults([]);

      setGrandTotal(
        0
      );
    };

  /* =========================================================
     EXPORT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleExport =
    () => {
      if (
        !results.length
      ) {
        toast.warn(
          "No data available to export"
        );

        return;
      }

      const headers = [
        "Bill Pass No",
        "Date",
        "Supplier",
        "Branch",
        "Amount",
      ];

      const rows =
        results.map(
          (item) => [
            item
              .fld_BillPassNo ||
              "",

            item
              .fld_EntryDate ||
              "",

            item
              .fld_suppliername ||
              "",

            item
              .branchname ||
              "",

            item
              .fld_TotalValueNew ||
              item
                .fld_TotalValue ||
              "",
          ]
        );

      const csvContent =
        [
          headers,
          ...rows,
        ]
          .map((row) =>
            row
              .map(
                (col) =>
                  `"${String(
                    col
                  ).replace(
                    /"/g,
                    '""'
                  )}"`
              )
              .join(",")
          )
          .join("\n");

      const blob =
        new Blob(
          [
            csvContent,
          ],
          {
            type: "text/csv;charset=utf-8;",
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href =
        url;

      link.setAttribute(
        "download",
        "ho_bill_pass_report.csv"
      );

      document.body.appendChild(
        link
      );

      link.click();

      document.body.removeChild(
        link
      );

      window.URL.revokeObjectURL(
        url
      );
    };

  /* =========================================================
     FETCH BRANCHES
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchBranches =
    async () => {
      try {
        const res =
          await axios.get(
            `${API_BASE}/bindddl_rpt`,
            {
              params: {
                branchtype:
                  branchType ===
                  "HO"
                    ? "HO"
                    : "Branch",

                branchid:
                  branchId ||
                  "8",
              },

              headers:
                commonHeaders,
            }
          );

        const branches =
          Array.isArray(
            res.data
              ?.branches
          )
            ? res.data
                .branches
            : Array.isArray(
                res.data
              )
            ? res.data
            : [];

        setBranchesList(
          branches
        );
      } catch (
        error
      ) {
        console.error(
          "Branch loading error:",
          error
        );

        toast.error(
          "Failed to load branches"
        );
      }
    };

  /* =========================================================
     FETCH SUPPLIERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchSuppliers =
    async () => {
      try {
        const res =
          await axios.get(
            `${API_BASE}/supplierddl_rpt`,
            {
              headers:
                commonHeaders,
            }
          );

        const suppliers =
          Array.isArray(
            res.data
              ?.suppliers
          )
            ? res.data
                .suppliers
            : Array.isArray(
                res.data
              )
            ? res.data
            : [];

        setSuppliersList(
          suppliers
        );
      } catch (
        error
      ) {
        console.error(
          "Supplier loading error:",
          error
        );

        toast.error(
          "Failed to load suppliers"
        );
      }
    };

  useEffect(() => {
    fetchBranches();

    fetchSuppliers();
  }, [
    commonHeaders,
  ]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch =
    async () => {
      if (
        !fromDate ||
        !toDate
      ) {
        toast.warning(
          "Please select both from and to dates"
        );

        return;
      }

      try {
        setLoading(
          true
        );

        const params = {
          bpno:
            bpNo ||
            "%",

          supplier:
            supplier ||
            "%",

          branch:
            branch ||
            branchId ||
            "8",

          fromdate:
            formatDateForApi(
              fromDate
            ),

          todate:
            formatDateForApi(
              toDate
            ),
        };

        const res =
          await axios.get(
            `${API_BASE}/billpass_rpt`,
            {
              params,

              headers:
                commonHeaders,
            }
          );

        const data =
          Array.isArray(
            res.data
              ?.data
          )
            ? res.data
                .data
            : [];

        setResults(
          data
        );

        setGrandTotal(
          Number(
            res.data
              ?.grandTotal ||
              0
          )
        );

        toast.success(
          `Loaded ${data.length} record(s)`
        );
      } catch (
        error
      ) {
        console.error(
          "Bill Pass report error:",
          error
        );

        toast.error(
          "Failed to fetch Bill Pass report"
        );

        setResults([]);

        setGrandTotal(
          0
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  /* =========================================================
     PRINT REPORT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint =
    () => {
      const params = {
        bpno:
          bpNo ||
          "%",

        supplier:
          supplier ||
          "%",

        branch:
          branch ||
          branchId ||
          "8",

        from:
          fromDate
            ? formatDateForApi(
                fromDate
              ).replace(
                /\//g,
                "-"
              )
            : "",

        to:
          toDate
            ? formatDateForApi(
                toDate
              ).replace(
                /\//g,
                "-"
              )
            : "",
      };

      const query =
        new URLSearchParams(
          params
        ).toString();

      window.open(
        `/inventory/HoBillPass_ReportPrint?${query}`,
        "_blank"
      );
    };

  /* =========================================================
     VIEW / ROW PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleViewRow =
    (item) => {
      const params = {
        bpno:
          item
            .fld_BillPassNo ||
          "%",

        supplier:
          item
            .fld_supplierid ||
          supplier ||
          "%",

        branch:
          item
            .fld_Branchid ||
          branch ||
          branchId ||
          "8",

        from:
          fromDate
            ? formatDateForApi(
                fromDate
              ).replace(
                /\//g,
                "-"
              )
            : "",

        to:
          toDate
            ? formatDateForApi(
                toDate
              ).replace(
                /\//g,
                "-"
              )
            : "",
      };

      const query =
        new URLSearchParams(
          params
        ).toString();

      window.open(
        `/inventory/HoBillPass_ReportPrint?${query}`,
        "_blank"
      );
    };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
          SAME STYLE AS DEPARTMENT MASTER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            HO Bill Pass Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME CARD DESIGN AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* =================================================
              FORM HEADER
          ================================================= */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <FileText className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  HO Bill Pass Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Search and review Head Office bill pass records
                </p>
              </div>
            </div>

            {/* ===============================================
                RIGHT HEADER BUTTONS
                EXPORT PRESERVED
            =============================================== */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={
                    14
                  }
                />

                Export
              </button>

              {/* SHOW / HIDE FORM */}

            <button
  type="button"
  onClick={() => setShowForm(!showForm)}
  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
    isDark
      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
      : "border-slate-300 text-slate-700 hover:bg-slate-100"
  }`}
>
  <FaFilter size={14} />

  {showForm ? "Hide Filters" : "Show Filters"}
</button>
            </div>
          </div>

          {/* =================================================
              FORM BODY
          ================================================= */}

          {showForm && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div
                className="
                  grid
                  grid-cols-1
                  md:grid-cols-2
                  lg:grid-cols-3
                  gap-5
                "
              >

                {/* FROM DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(
                      e
                    ) =>
                      setFromDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(
                      e
                    ) =>
                      setToDate(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BILL PASS NO */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Bill Pass No
                  </label>

                  <input
                    type="text"
                    value={
                      bpNo
                    }
                    onChange={(
                      e
                    ) =>
                      setBpNo(
                        e.target
                          .value
                      )
                    }
                    placeholder="Enter bill pass number"
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* BRANCH */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      branch
                    }
                    onChange={(
                      e
                    ) =>
                      setBranch(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="8">
                      Select Branch
                    </option>

                    {branchesList.map(
                      (
                        b
                      ) => (
                        <option
                          key={
                            b.fld_Branchid ||
                            b.fld_branchid
                          }
                          value={
                            b.fld_Branchid ||
                            b.fld_branchid
                          }
                        >
                          {b.fld_BranchName ||
                            b.fld_branchname}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUPPLIER */}

                <div>
                  <label
                    className={
                      labelClass
                    }
                  >
                    Supplier
                  </label>

                  <select
                    value={
                      supplier
                    }
                    onChange={(
                      e
                    ) =>
                      setSupplier(
                        e.target
                          .value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Supplier
                    </option>

                    {suppliersList.map(
                      (
                        s
                      ) => (
                        <option
                          key={
                            s.fld_supplierid
                          }
                          value={
                            s.fld_supplierid
                          }
                        >
                          {
                            s.fld_suppliername
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  FORM BUTTONS
                  SAME POSITION AS DEPARTMENT MASTER
              ============================================= */}

              <div
                className="
                  flex
                  flex-wrap
                  justify-end
                  gap-3
                  mt-6
                  pt-5
                  border-t
                  border-slate-200
                  dark:border-slate-700
                "
              >

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={
                      14
                    }
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2.5
                    text-sm
                    font-semibold
                    rounded-xl
                    transition
                    ${
                      isDark
                        ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                        : "bg-green-100 text-green-700 hover:bg-green-200"
                    }
                  `}
                >
                  <FaPrint
                    size={
                      14
                    }
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span
                      className="
                        w-4
                        h-4
                        border-2
                        border-white
                        border-t-transparent
                        rounded-full
                        animate-spin
                      "
                    />
                  ) : (
                    <FaSearch
                      size={
                        14
                      }
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
            SAME STYLE AS DEPARTMENT MASTER
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* =================================================
              TABLE HEADER
          ================================================= */}

          <div
            className="
              p-6
              border-b
              border-gray-200
              dark:border-gray-700
            "
          >
            <div
              className="
                flex
                flex-col
                sm:flex-row
                items-start
                sm:items-center
                justify-between
                gap-4
              "
            >

              {/* LEFT */}

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  HO Bill Pass Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {
                    results.length
                  }{" "}
                  records
                </span>
              </div>

              {/* GRAND TOTAL */}

              {results.length >
                0 && (
                <div
                  className={`px-4 py-2 rounded-xl text-sm font-bold ${
                    isDark
                      ? "bg-green-900/30 text-green-300"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  Grand Total:{" "}
                  {formatCurrency(
                    grandTotal
                  )}
                </div>
              )}
            </div>
          </div>

          {/* =================================================
              RESULTS
          ================================================= */}

          {loading ? (
            <div
              className="
                flex
                flex-col
                items-center
                justify-center
                py-16
              "
            >
              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading bill pass records...
              </p>
            </div>
          ) : results.length >
            0 ? (
            <>

              {/* =============================================
                  TABLE
              ============================================= */}

              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  {/* HEADER */}

                  <thead
                    className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                  >
                    <tr>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Sl No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Bill Pass No
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Date
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Supplier
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Branch
                      </th>

                      <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                        Amount
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        View
                      </th>

                      <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                        Print
                      </th>
                    </tr>
                  </thead>

                  {/* BODY */}

                  <tbody
                    className={`divide-y ${
                      isDark
                        ? "divide-gray-600"
                        : "divide-gray-200"
                    }`}
                  >
                    {results.map(
                      (
                        item,
                        idx
                      ) => (
                        <tr
                          key={
                            item
                              .fld_BillPassNo ||
                            idx
                          }
                          className={`transition ${
                            isDark
                              ? "bg-gray-800/20 hover:bg-gray-700/30"
                              : "bg-white hover:bg-gray-50"
                          }`}
                        >

                          {/* SL NO */}

                          <td className="px-4 py-3 font-medium">
                            {idx +
                              1}
                          </td>

                          {/* BILL PASS */}

                          <td className="px-4 py-3">

                            <span
                              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isDark
                                  ? "bg-gray-700 text-gray-200"
                                  : `bg-${baseColor}-100 text-${baseColor}-800`
                              }`}
                            >
                              {item.fld_BillPassNo ||
                                "-"}
                            </span>
                          </td>

                          {/* DATE */}

                          <td className="px-4 py-3 whitespace-nowrap">

                            {item.fld_EntryDate ||
                              formatDate(
                                item.fld_date
                              )}
                          </td>

                          {/* SUPPLIER */}

                          <td className="px-4 py-3 font-medium">

                            {item.fld_suppliername ||
                              "-"}
                          </td>

                          {/* BRANCH */}

                          <td className="px-4 py-3">

                            {item.branchname ||
                              "-"}
                          </td>

                          {/* AMOUNT */}

                          <td
                            className={`px-4 py-3 whitespace-nowrap font-semibold ${
                              isDark
                                ? "text-green-300"
                                : "text-green-700"
                            }`}
                          >
                            {formatCurrency(
                              item.fld_TotalValueNew ||
                                item.fld_TotalValue
                            )}
                          </td>

                          {/* VIEW */}

                          <td className="px-4 py-3 text-center">

                            <button
                              type="button"
                              onClick={() =>
                                handleViewRow(
                                  item
                                )
                              }
                              className={`p-1.5 rounded-full transition ${
                                isDark
                                  ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                  : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                              }`}
                              title="View"
                            >
                              <FaEye
                                className="w-4 h-4"
                              />
                            </button>
                          </td>

                          {/* PRINT */}

                          <td className="px-4 py-3 text-center">

                            <button
                              type="button"
                              onClick={() =>
                                handleViewRow(
                                  item
                                )
                              }
                              className={`p-1.5 rounded-full transition ${
                                isDark
                                  ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                                  : "bg-green-100 text-green-600 hover:bg-green-200"
                              }`}
                              title="Print"
                            >
                              <FaFileInvoiceDollar
                                className="w-4 h-4"
                              />
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>

                  {/* =========================================
                      GRAND TOTAL FOOTER
                      ORIGINAL FUNCTIONALITY PRESERVED
                  ========================================= */}

                  <tfoot
                    className={
                      isDark
                        ? "bg-gray-900"
                        : "bg-gray-100"
                    }
                  >
                    <tr>

                      <td
                        colSpan={
                          5
                        }
                        className={`px-4 py-3 text-right text-sm font-bold ${
                          isDark
                            ? "text-white"
                            : "text-gray-900"
                        }`}
                      >
                        Grand Total
                      </td>

                      <td
                        className={`px-4 py-3 text-sm font-bold ${
                          isDark
                            ? "text-green-300"
                            : "text-green-700"
                        }`}
                      >
                        {formatCurrency(
                          grandTotal
                        )}
                      </td>

                      <td
                        colSpan={
                          2
                        }
                      ></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          ) : (
            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div
              className="
                px-4
                py-14
                text-center
              "
            >
              <FaFileInvoiceDollar
                size={
                  40
                }
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <p
                className={`text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                No records found. Please use the filters above to search bill pass records.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={
          3000
        }
        hideProgressBar={
          false
        }
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default HOBillPassReport;